// --- Calls an agent placed to the owner, kept in SQLite ---
//
// One row per call, so a missed call is still there after a restart and
// the dashboard can list them. Nothing else reads this table. A camera
// ask (#325 phase 3: "show me") is a call of kind "camera": same
// allowlist, same hourly limit, same lifecycle, answered on the phone
// instead of the voice widget.

import type Database from "better-sqlite3"

export type CallStatus = "ringing" | "answered" | "ended" | "declined" | "missed" | "later"
export type CallUrgency = "normal" | "urgent"
/** "voice": a live voice call on the widget. "camera": the agent asks to see through the phone camera. */
export type CallKind = "voice" | "camera"

export interface Call {
  id: string
  agentId: string
  kind: CallKind
  reason: string
  urgency: CallUrgency
  status: CallStatus
  /** The channel and chat the asking turn ran in, so what the agent sees
   *  is answered there; null when the turn had none (a task run). */
  channel: string | null
  chatId: string | null
  /** When the agent asked, ms since the epoch. */
  createdAt: number
  /** When it last started ringing: at the request, or when "later" came due. */
  ringingSince: number | null
  answeredAt: number | null
  endedAt: number | null
  /** Status "later": when it rings again. */
  ringAgainAt: number | null
  /** Why it ended the way it did, in a few words ("in Focus", "not answered"). */
  note: string | null
  /** The agent's own summary, written after hang-up. */
  summary: string | null
}

/** A call waiting for a busy owner: since when, and when the owner was
 *  last told. Ms since the epoch. */
export interface Waiting {
  since: number
  toldAt: number
}

/** A call that stops ringing (answered, snoozed, missed...) is no longer
 *  waiting: the mark goes in the same write, so a call that rings again
 *  later starts a fresh wait, even across a restart. */
function leaving(patch: { status?: CallStatus }): string {
  return patch.status && patch.status !== "ringing" ? ", waiting_since=NULL, told_at=NULL" : ""
}

/** Calls still in progress: an agent has at most one of these. */
export const LIVE: CallStatus[] = ["ringing", "answered", "later"]

const COLUMNS = `id, agent_id AS agentId, kind, reason, urgency, status, created_at AS createdAt,
  ringing_since AS ringingSince, answered_at AS answeredAt, ended_at AS endedAt,
  ring_again_at AS ringAgainAt, note, summary, channel, chat_id AS chatId`

export class CallStore {
  constructor(private db: Database.Database) {
    db.exec(`CREATE TABLE IF NOT EXISTS calls (
      id TEXT PRIMARY KEY, agent_id TEXT NOT NULL, reason TEXT NOT NULL,
      urgency TEXT NOT NULL, status TEXT NOT NULL, created_at INTEGER NOT NULL,
      ringing_since INTEGER, answered_at INTEGER, ended_at INTEGER,
      ring_again_at INTEGER, note TEXT, summary TEXT);
      CREATE INDEX IF NOT EXISTS calls_recent ON calls(created_at DESC);`)
    // Columns added since the table first shipped (#325 phase 3). A row
    // from before is a voice call from a turn whose chat was not kept.
    const have = new Set((db.prepare("PRAGMA table_info(calls)").all() as Array<{ name: string }>).map((c) => c.name))
    if (!have.has("kind")) db.exec("ALTER TABLE calls ADD COLUMN kind TEXT NOT NULL DEFAULT 'voice'")
    if (!have.has("channel")) db.exec("ALTER TABLE calls ADD COLUMN channel TEXT")
    if (!have.has("chat_id")) db.exec("ALTER TABLE calls ADD COLUMN chat_id TEXT")
    // A call waiting for a busy owner (#969), kept so a restart does not
    // forget it: since when it waits, and when the owner was last told.
    if (!have.has("waiting_since")) db.exec("ALTER TABLE calls ADD COLUMN waiting_since INTEGER")
    if (!have.has("told_at")) db.exec("ALTER TABLE calls ADD COLUMN told_at INTEGER")
  }

  /** Ringing calls marked as waiting for a busy owner, by id. */
  waiting(): Map<string, Waiting> {
    const rows = this.db.prepare("SELECT id, waiting_since AS since, told_at AS toldAt FROM calls WHERE waiting_since IS NOT NULL AND status='ringing'")
      .all() as Array<{ id: string } & Waiting>
    return new Map(rows.map(({ id, since, toldAt }) => [id, { since, toldAt: toldAt ?? since }]))
  }

  /** Mark a call as waiting, or clear the mark with null. */
  setWaiting(id: string, w: Waiting | null): void {
    this.db.prepare("UPDATE calls SET waiting_since=?, told_at=? WHERE id=?").run(w?.since ?? null, w?.toldAt ?? null, id)
  }

  insert(call: Call): void {
    this.db.prepare(`INSERT INTO calls (id, agent_id, kind, reason, urgency, status, created_at, ringing_since,
      answered_at, ended_at, ring_again_at, note, summary, channel, chat_id) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`).run(
      call.id, call.agentId, call.kind, call.reason, call.urgency, call.status, call.createdAt,
      call.ringingSince, call.answeredAt, call.endedAt, call.ringAgainAt, call.note, call.summary, call.channel, call.chatId)
  }

  get(id: string): Call | undefined {
    return this.db.prepare(`SELECT ${COLUMNS} FROM calls WHERE id=?`).get(id) as Call | undefined
  }

  /** Write the changed fields of one call. */
  update(id: string, patch: Partial<Omit<Call, "id" | "agentId" | "createdAt">>): Call | undefined {
    const cols: Record<string, string> = {
      reason: "reason", urgency: "urgency", status: "status", ringingSince: "ringing_since",
      answeredAt: "answered_at", endedAt: "ended_at", ringAgainAt: "ring_again_at", note: "note", summary: "summary",
    }
    const keys = Object.keys(patch).filter((k) => k in cols)
    if (keys.length) {
      const set = keys.map((k) => `${cols[k]}=?`).join(", ") + leaving(patch)
      this.db.prepare(`UPDATE calls SET ${set} WHERE id=?`).run(...keys.map((k) => (patch as any)[k] ?? null), id)
    }
    return this.get(id)
  }

  /** Change a call only if it is still `from`: the check and the write are
   *  one statement, so two sweeps, or a sweep and the owner answering,
   *  cannot both move the same call. Undefined when it had moved on. */
  transition(id: string, from: CallStatus | CallStatus[], patch: Partial<Omit<Call, "id" | "agentId" | "createdAt">>): Call | undefined {
    const statuses = Array.isArray(from) ? from : [from]
    const cols: Record<string, string> = {
      status: "status", ringingSince: "ringing_since", answeredAt: "answered_at", endedAt: "ended_at",
      ringAgainAt: "ring_again_at", note: "note", summary: "summary",
    }
    const keys = Object.keys(patch).filter((k) => k in cols)
    const set = keys.map((k) => `${cols[k]}=?`).join(", ") + leaving(patch)
    const marks = statuses.map(() => "?").join(",")
    const changed = this.db.prepare(`UPDATE calls SET ${set} WHERE id=? AND status IN (${marks})`)
      .run(...keys.map((k) => (patch as any)[k] ?? null), id, ...statuses).changes
    return changed ? this.get(id) : undefined
  }

  /** Newest first; only these statuses, and only this kind, when given. */
  list(opts: { status?: CallStatus[]; kind?: CallKind; limit?: number } = {}): Call[] {
    const limit = Math.min(Math.max(opts.limit ?? 50, 1), 200)
    const where: string[] = []
    const args: unknown[] = []
    if (opts.status?.length) {
      where.push(`status IN (${opts.status.map(() => "?").join(",")})`)
      args.push(...opts.status)
    }
    if (opts.kind) { where.push("kind=?"); args.push(opts.kind) }
    const filter = where.length ? `WHERE ${where.join(" AND ")}` : ""
    return this.db.prepare(`SELECT ${COLUMNS} FROM calls ${filter} ORDER BY created_at DESC, rowid DESC LIMIT ?`)
      .all(...args, limit) as Call[]
  }

  /** Calls this agent placed after `since` (ms), for the hourly limit. */
  countSince(agentId: string, since: number): number {
    return (this.db.prepare("SELECT COUNT(*) AS n FROM calls WHERE agent_id=? AND created_at>?")
      .get(agentId, since) as { n: number }).n
  }
}
